/* eslint-disable unicode-bom */
import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import DesignationSelect from './DesignationSelect';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Lock,
  Mail,
  Phone,
  Eye,
  EyeOff,
  ArrowLeft,
  ShieldCheck,
  CheckCircle,
  XCircle,
} from 'lucide-react';

const ALLOWED_DOMAINS = ['ksp.gov.in', 'gov.in', 'dsci.in', 'ka.gov.in'];

// Basic RFC-ish format check: non-empty local part, a domain, and a TLD.
function isValidEmailFormat(email) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test((email || '').trim());
}

function isAllowedEmail(email) {
  if (!isValidEmailFormat(email)) return false;
  const domain = email.trim().split('@')[1]?.toLowerCase() || '';
  return ALLOWED_DOMAINS.some((d) => domain === d || domain.endsWith('.' + d));
}

function isCugNumber(value) {
  return /^\d{10}$/.test((value || '').trim());
}

// Classify a forgot-password identifier: '@' present => treat as email, else CUG.
function looksLikeEmail(value) {
  return (value || '').includes('@');
}

function passwordChecklist(pw) {
  return {
    length: pw.length >= 8,
    uppercase: /[A-Z]/.test(pw),
    lowercase: /[a-z]/.test(pw),
    number: /\d/.test(pw),
    special: /[!@#$%^&*(),.?":{}|<>]/.test(pw),
  };
}

function isStrongPassword(pw) {
  const c = passwordChecklist(pw);
  return c.length && c.uppercase && c.lowercase && c.number && c.special;
}

function extractError(error, fallback = 'Something went wrong') {
  const detail = error?.response?.data?.detail;
  if (Array.isArray(detail)) {
    return detail.map((e) => (typeof e === 'object' && e.msg ? e.msg : e)).join(', ');
  }
  if (typeof detail === 'string') return detail;
  return fallback;
}

// ============================================================================
// Sub-components
// ============================================================================

const OtpInput = ({ value, onChange, length = 6, autoFocus = true, disabled = false }) => {
  const inputsRef = useRef([]);

  useEffect(() => {
    if (autoFocus) inputsRef.current[0]?.focus();
  }, [autoFocus]);

  const handleChange = (i, v) => {
    const digit = v.replace(/\D/g, '').slice(-1);
    const next = (value + '').padEnd(length, ' ').split('');
    next[i] = digit || ' ';
    const joined = next.join('').replace(/\s/g, '');
    onChange(joined);
    if (digit && i < length - 1) inputsRef.current[i + 1]?.focus();
  };

  const handleKeyDown = (i, e) => {
    if (e.key === 'Backspace' && !value[i] && i > 0) {
      inputsRef.current[i - 1]?.focus();
    } else if (e.key === 'ArrowLeft' && i > 0) {
      inputsRef.current[i - 1]?.focus();
    } else if (e.key === 'ArrowRight' && i < length - 1) {
      inputsRef.current[i + 1]?.focus();
    }
  };

  const handlePaste = (e) => {
    const text = (e.clipboardData.getData('text') || '').replace(/\D/g, '').slice(0, length);
    if (!text) return;
    e.preventDefault();
    onChange(text);
    const focusIdx = Math.min(text.length, length - 1);
    inputsRef.current[focusIdx]?.focus();
  };

  return (
    <div
      className="otp-input-row"
      style={{ gridTemplateColumns: `repeat(${length}, minmax(0, 1fr))` }}
      onPaste={handlePaste}
    >
      {Array.from({ length }).map((_, i) => (
        <input
          key={i}
          ref={(el) => (inputsRef.current[i] = el)}
          type="text"
          inputMode="numeric"
          maxLength={1}
          value={value[i] || ''}
          onChange={(e) => handleChange(i, e.target.value)}
          onKeyDown={(e) => handleKeyDown(i, e)}
          disabled={disabled}
          className="otp-input-box"
          aria-label={`OTP digit ${i + 1}`}
          autoComplete="one-time-code"
        />
      ))}
    </div>
  );
};

const PasswordRules = ({ pw }) => {
  const checks = passwordChecklist(pw);
  const items = [
    { key: 'length', text: 'At least 8 characters' },
    { key: 'uppercase', text: 'One uppercase letter' },
    { key: 'lowercase', text: 'One lowercase letter' },
    { key: 'number', text: 'One number' },
    { key: 'special', text: 'One special character (!@#$%...)' },
  ];
  return (
    <div className="bg-slate-50 rounded-xl p-4 space-y-1.5" role="list" aria-label="Password requirements">
      <p className="text-xs font-semibold text-gray-600 mb-2">Password Requirements</p>
      {items.map(({ key, text }) => (
        <div key={key} className="flex items-center gap-2" role="listitem">
          {checks[key] ? (
            <CheckCircle className="w-3.5 h-3.5 text-emerald-500 shrink-0" aria-hidden="true" />
          ) : (
            <XCircle className="w-3.5 h-3.5 text-gray-300 shrink-0" aria-hidden="true" />
          )}
          <span className={`text-xs ${checks[key] ? 'text-emerald-600' : 'text-gray-500'}`}>{text}</span>
        </div>
      ))}
    </div>
  );
};

// ============================================================================
// Main component
// ============================================================================

const VIEWS = {
  LOGIN: 'login',
  SIGNUP_FORM: 'signup-form',
  SIGNUP_OTP: 'signup-otp',
  SIGNUP_EMAIL_OTP: 'signup-email-otp',
  FORGOT_ID: 'forgot-id',
  FORGOT_OTP: 'forgot-otp',
  FORGOT_RESET: 'forgot-reset',
};

const Login = () => {
  const [view, setView] = useState(VIEWS.LOGIN);

  // Shared
  const [loading, setLoading] = useState(false);

  // Login
  const [loginId, setLoginId] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  // Signup
  const [designation, setDesignation] = useState('');
  const [cugNumber, setCugNumber] = useState('');
  const [email, setEmail] = useState('');
  const [signupPassword, setSignupPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showSignupPassword, setShowSignupPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // OTP state (used by signup mobile, signup email, and forgot)
  const [otpCode, setOtpCode] = useState('');
  const [otpExpiresAt, setOtpExpiresAt] = useState(null);     // ms timestamp
  const [resendAvailableAt, setResendAvailableAt] = useState(null);
  // Mobile OTP token captured at signup step 2, consumed when the email step completes.
  const [mobileVerificationToken, setMobileVerificationToken] = useState('');
  // Tick is intentionally only used to force re-renders for the countdown timers.
  // eslint-disable-next-line no-unused-vars
  const [tick, setTick] = useState(0);

  // Forgot-password state
  const [forgotIdentifier, setForgotIdentifier] = useState('');
  const [forgotChannel, setForgotChannel] = useState('sms'); // 'sms' | 'email'
  const [resetToken, setResetToken] = useState('');
  const [resetPassword, setResetPassword] = useState('');
  const [resetConfirm, setResetConfirm] = useState('');
  const [showResetPassword, setShowResetPassword] = useState(false);
  const [showResetConfirm, setShowResetConfirm] = useState(false);

  const {
    login,
    register,
    currentUser,
    sendRegistrationOtp,
    verifyRegistrationOtp,
    sendRegistrationEmailOtp,
    verifyRegistrationEmailOtp,
    forgotPasswordSendOtp,
    forgotPasswordVerifyOtp,
    forgotPasswordReset,
  } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (currentUser) navigate('/');
  }, [currentUser, navigate]);

  // 1s ticker so the resend timer + expiry countdown re-render.
  useEffect(() => {
    const otpViews = [VIEWS.SIGNUP_OTP, VIEWS.SIGNUP_EMAIL_OTP, VIEWS.FORGOT_OTP];
    if (!otpViews.includes(view)) return undefined;
    const id = setInterval(() => setTick((t) => t + 1), 1000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view]);

  const resetAllState = () => {
    setOtpCode('');
    setOtpExpiresAt(null);
    setResendAvailableAt(null);
    setMobileVerificationToken('');
  };

  const secondsUntil = (ts) => {
    if (!ts) return 0;
    return Math.max(0, Math.ceil((ts - Date.now()) / 1000));
  };

  // --------------------------------------------------------------------------
  // LOGIN
  // --------------------------------------------------------------------------
  const handleLogin = async (e) => {
    e.preventDefault();
    if (!loginId || !password) {
      toast.error('Please fill in all fields');
      return;
    }
    try {
      setLoading(true);
      await login(loginId, password);
      toast.success('Successfully logged in!');
    } catch (error) {
      toast.error(extractError(error, 'Failed to log in'));
    } finally {
      setLoading(false);
    }
  };

  // --------------------------------------------------------------------------
  // SIGNUP — step 1: form validate + request OTP
  // --------------------------------------------------------------------------
  const handleSignupRequestOtp = async (e) => {
    e.preventDefault();
    if (!designation.trim() || !cugNumber || !email || !signupPassword || !confirmPassword) {
      toast.error('Please fill in all fields');
      return;
    }
    if (!isCugNumber(cugNumber)) {
      toast.error('CUG number must be exactly 10 digits');
      return;
    }
    if (!isValidEmailFormat(email)) {
      toast.error('Please enter a valid email address');
      return;
    }
    if (!isAllowedEmail(email)) {
      toast.error('Email must end with @ksp.gov.in, @ka.gov.in, @gov.in, or @dsci.in');
      return;
    }
    if (!isStrongPassword(signupPassword)) {
      toast.error('Password must include uppercase, lowercase, number, and special character (8+ chars)');
      return;
    }
    if (signupPassword !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    try {
      setLoading(true);
      const data = await sendRegistrationOtp(cugNumber, email);
      setOtpCode('');
      setOtpExpiresAt(Date.now() + (data.expires_in || 300) * 1000);
      setResendAvailableAt(Date.now() + (data.resend_after || 30) * 1000);
      setView(VIEWS.SIGNUP_OTP);
      toast.success(`OTP sent to mobile ending ${cugNumber.slice(-4)}`);
    } catch (error) {
      toast.error(extractError(error, 'Could not send OTP'));
    } finally {
      setLoading(false);
    }
  };

  // SIGNUP — step 2: verify mobile OTP, then move on to the email OTP step
  const handleSignupVerifyMobile = async (e) => {
    e.preventDefault();
    if (otpCode.length < 4) {
      toast.error('Enter the OTP from the SMS');
      return;
    }
    try {
      setLoading(true);
      // 1) Verify the mobile OTP. This consumes the SMS OTP, so it must only
      //    run once — on success we capture the token and never re-verify.
      const verifyResp = await verifyRegistrationOtp(cugNumber, otpCode);
      setMobileVerificationToken(verifyResp.verification_token);

      // 2) Advance to the email step regardless, then send the email OTP.
      //    If the email send fails (e.g. a transient mail error), the user can
      //    recover with "Resend OTP" on the email screen — they never have to
      //    re-verify the mobile OTP (which would already be consumed).
      setOtpCode('');
      setView(VIEWS.SIGNUP_EMAIL_OTP);
      try {
        const data = await sendRegistrationEmailOtp(email);
        setOtpExpiresAt(Date.now() + (data.expires_in || 300) * 1000);
        setResendAvailableAt(Date.now() + (data.resend_after || 30) * 1000);
        toast.success('Mobile verified. OTP sent to your email.');
      } catch (err) {
        // Mobile is already verified; allow an immediate email resend.
        setOtpExpiresAt(null);
        setResendAvailableAt(null);
        toast.error(extractError(err, 'Mobile verified, but the email OTP could not be sent. Tap “Resend OTP”.'));
      }
    } catch (error) {
      toast.error(extractError(error, 'OTP verification failed'));
    } finally {
      setLoading(false);
    }
  };

  const handleSignupResendOtp = async () => {
    if (secondsUntil(resendAvailableAt) > 0) return;
    try {
      setLoading(true);
      const data = await sendRegistrationOtp(cugNumber, email);
      setOtpCode('');
      setOtpExpiresAt(Date.now() + (data.expires_in || 300) * 1000);
      setResendAvailableAt(Date.now() + (data.resend_after || 30) * 1000);
      toast.success('A new OTP has been sent.');
    } catch (error) {
      toast.error(extractError(error, 'Could not resend OTP'));
    } finally {
      setLoading(false);
    }
  };

  // SIGNUP — step 3: verify email OTP, then submit registration with both tokens
  const handleSignupVerifyEmailAndRegister = async (e) => {
    e.preventDefault();
    if (otpCode.length < 4) {
      toast.error('Enter the OTP from your email');
      return;
    }
    try {
      setLoading(true);
      const verifyResp = await verifyRegistrationEmailOtp(email, otpCode);
      const emailToken = verifyResp.verification_token;
      await register(designation.trim(), cugNumber, email, signupPassword, mobileVerificationToken, emailToken);
      toast.success('Registration successful! Please wait for admin approval.');
      // Reset and return to login
      setDesignation('');
      setCugNumber('');
      setEmail('');
      setSignupPassword('');
      setConfirmPassword('');
      resetAllState();
      setView(VIEWS.LOGIN);
    } catch (error) {
      toast.error(extractError(error, 'Could not complete registration'));
    } finally {
      setLoading(false);
    }
  };

  const handleSignupResendEmailOtp = async () => {
    if (secondsUntil(resendAvailableAt) > 0) return;
    try {
      setLoading(true);
      const data = await sendRegistrationEmailOtp(email);
      setOtpCode('');
      setOtpExpiresAt(Date.now() + (data.expires_in || 300) * 1000);
      setResendAvailableAt(Date.now() + (data.resend_after || 30) * 1000);
      toast.success('A new OTP has been sent to your email.');
    } catch (error) {
      toast.error(extractError(error, 'Could not resend OTP'));
    } finally {
      setLoading(false);
    }
  };

  // --------------------------------------------------------------------------
  // FORGOT PASSWORD — step 1: identifier
  // --------------------------------------------------------------------------
  // Validate a forgot-password identifier and resolve the delivery channel.
  // Returns { ok, channel } — channel is 'email' for an email, 'sms' for a CUG.
  const resolveForgotIdentifier = (raw) => {
    const id = (raw || '').trim();
    if (!id) {
      toast.error('Please enter your CUG number or email address');
      return { ok: false };
    }
    if (looksLikeEmail(id)) {
      if (!isAllowedEmail(id)) {
        toast.error('Enter a valid official email (@ksp.gov.in, @ka.gov.in, @gov.in, or @dsci.in)');
        return { ok: false };
      }
      return { ok: true, channel: 'email' };
    }
    if (!isCugNumber(id)) {
      toast.error('Enter your 10-digit CUG number or a valid email address');
      return { ok: false };
    }
    return { ok: true, channel: 'sms' };
  };

  const handleForgotSendOtp = async (e) => {
    e.preventDefault();
    const id = forgotIdentifier.trim();
    const { ok, channel } = resolveForgotIdentifier(id);
    if (!ok) return;
    setForgotChannel(channel); // drives the heading + OTP-screen copy
    try {
      setLoading(true);
      const data = await forgotPasswordSendOtp(id, channel);
      setOtpCode('');
      setOtpExpiresAt(Date.now() + (data.expires_in || 300) * 1000);
      setResendAvailableAt(Date.now() + (data.resend_after || 30) * 1000);
      setView(VIEWS.FORGOT_OTP);
      toast.success(data.message || 'OTP sent.');
    } catch (error) {
      // 404 = unknown identifier; show the server's message so the user knows
      // to register first rather than retrying with the same number.
      toast.error(extractError(error, 'Could not send OTP'));
    } finally {
      setLoading(false);
    }
  };

  // FORGOT PASSWORD — step 2: verify OTP, get reset token
  const handleForgotVerifyOtp = async (e) => {
    e.preventDefault();
    if (otpCode.length < 4) {
      toast.error(forgotChannel === 'email' ? 'Enter the OTP from your email' : 'Enter the OTP from the SMS');
      return;
    }
    try {
      setLoading(true);
      const data = await forgotPasswordVerifyOtp(forgotIdentifier.trim(), otpCode);
      setResetToken(data.verification_token);
      setView(VIEWS.FORGOT_RESET);
    } catch (error) {
      toast.error(extractError(error, 'OTP verification failed'));
    } finally {
      setLoading(false);
    }
  };

  const handleForgotResendOtp = async () => {
    if (secondsUntil(resendAvailableAt) > 0) return;
    const { ok, channel } = resolveForgotIdentifier(forgotIdentifier);
    if (!ok) return;
    try {
      setLoading(true);
      const data = await forgotPasswordSendOtp(forgotIdentifier.trim(), channel);
      setOtpCode('');
      setOtpExpiresAt(Date.now() + (data.expires_in || 300) * 1000);
      setResendAvailableAt(Date.now() + (data.resend_after || 30) * 1000);
      toast.success('A new OTP has been sent.');
    } catch (error) {
      toast.error(extractError(error, 'Could not resend OTP'));
    } finally {
      setLoading(false);
    }
  };

  // FORGOT PASSWORD — step 3: new password
  const handleForgotReset = async (e) => {
    e.preventDefault();
    if (!isStrongPassword(resetPassword)) {
      toast.error('Password does not meet the requirements');
      return;
    }
    if (resetPassword !== resetConfirm) {
      toast.error('Passwords do not match');
      return;
    }
    try {
      setLoading(true);
      await forgotPasswordReset(resetToken, resetPassword);
      toast.success('Password reset successfully. You can now sign in.');
      // Cleanup
      setForgotIdentifier('');
      setForgotChannel('sms');
      setResetToken('');
      setResetPassword('');
      setResetConfirm('');
      resetAllState();
      setView(VIEWS.LOGIN);
    } catch (error) {
      toast.error(extractError(error, 'Could not reset password'));
    } finally {
      setLoading(false);
    }
  };

  // --------------------------------------------------------------------------
  // RENDER HELPERS
  // --------------------------------------------------------------------------
  const headingFor = () => {
    switch (view) {
      case VIEWS.SIGNUP_FORM: return { title: 'Create Account', sub: 'Register for platform access' };
      case VIEWS.SIGNUP_OTP: return { title: 'Verify your mobile', sub: `Enter the 6-digit OTP sent to ${cugNumber.slice(0, 2)}******${cugNumber.slice(-2)}` };
      case VIEWS.SIGNUP_EMAIL_OTP: return { title: 'Verify your email', sub: `Enter the 6-digit OTP sent to ${email}` };
      case VIEWS.FORGOT_ID: return { title: 'Forgot Password', sub: 'Enter your CUG number or email — we\'ll send the OTP there' };
      case VIEWS.FORGOT_OTP: return { title: 'Verify OTP', sub: forgotChannel === 'email' ? 'Enter the OTP sent to your registered email' : 'Enter the OTP sent to your registered mobile number' };
      case VIEWS.FORGOT_RESET: return { title: 'Set a new password', sub: 'Choose a strong password to secure your account' };
      default: return { title: 'Welcome Back', sub: 'Sign in to your account' };
    }
  };
  const heading = headingFor();

  const otpExpirySecs = secondsUntil(otpExpiresAt);
  const resendSecs = secondsUntil(resendAvailableAt);

  // --------------------------------------------------------------------------
  // RENDER
  // --------------------------------------------------------------------------
  return (
    <div className="login-shell" role="main">
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="login-card"
      >
        {/* Brand panel (desktop only) */}
        <div className="login-brand" aria-hidden="true" style={{ flex: '0 0 44%' }}>
          <div className="absolute top-0 right-0 w-64 h-64 bg-white/5 rounded-full -mr-32 -mt-32 pointer-events-none" />
          <div className="absolute bottom-0 left-0 w-96 h-96 bg-white/5 rounded-full -ml-48 -mb-48 pointer-events-none" />

          <div className="relative z-10 text-center px-4">
            <motion.img
              src="/ccitr-logo.png"
              alt=""
              className="h-48 w-auto mx-auto drop-shadow-2xl mb-8"
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ duration: 0.7 }}
            />
            <h1 className="text-3xl font-bold text-white mb-2 tracking-tight">CCITR IntelliBot</h1>
            <p className="text-blue-200 text-sm leading-relaxed max-w-xs mx-auto mb-8">
              AI-powered assistant for cybercrime investigation, digital forensics, and access to CID circulars and standing orders.
            </p>
            <div className="inline-flex items-center gap-2 bg-white/10 rounded-full px-4 py-1.5 text-xs text-blue-200">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
              Authorised personnel only
            </div>
          </div>

          <p className="absolute bottom-5 text-blue-300/60 text-xs">&copy; 2026 CCITR. All rights reserved.</p>
        </div>

        {/* Form panel */}
        <div className="login-form-col">
          {/* Mobile logo */}
          <div className="md:hidden text-center mb-7">
            <div className="inline-block bg-gradient-to-br from-slate-900 to-blue-900 rounded-2xl p-5 mb-3 shadow-lg">
              <img src="/ccitr-logo.png" alt="CCITR Logo" className="h-14 w-auto mx-auto" />
            </div>
            <h1 className="text-xl font-bold text-gray-900">CCITR IntelliBot</h1>
            <p className="text-gray-500 text-sm mt-1">Intelligent Conversation Platform</p>
          </div>

          {/* Back-arrow for non-default views */}
          {view !== VIEWS.LOGIN && (
            <button
              type="button"
              onClick={() => {
                if (view === VIEWS.SIGNUP_OTP) { setView(VIEWS.SIGNUP_FORM); return; }
                if (view === VIEWS.SIGNUP_EMAIL_OTP) { setOtpCode(''); setView(VIEWS.SIGNUP_OTP); return; }
                if (view === VIEWS.SIGNUP_FORM) { setView(VIEWS.LOGIN); return; }
                if (view === VIEWS.FORGOT_OTP) { setView(VIEWS.FORGOT_ID); return; }
                if (view === VIEWS.FORGOT_ID) { setView(VIEWS.LOGIN); return; }
                if (view === VIEWS.FORGOT_RESET) { setView(VIEWS.FORGOT_OTP); return; }
                setView(VIEWS.LOGIN);
              }}
              className="inline-flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700 mb-3"
            >
              <ArrowLeft className="h-4 w-4" /> Back
            </button>
          )}

          {/* Heading */}
          <AnimatePresence mode="wait">
            <motion.div
              key={view}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              transition={{ duration: 0.2 }}
              className="mb-6"
            >
              <h2 className="text-2xl font-bold text-gray-900">{heading.title}</h2>
              <p className="text-gray-500 text-sm mt-1">{heading.sub}</p>
            </motion.div>
          </AnimatePresence>

          {/* ===================================================== LOGIN view */}
          {view === VIEWS.LOGIN && (
            <form onSubmit={handleLogin} className="space-y-4" noValidate>
              <div>
                <label htmlFor="login-cug" className="block text-sm font-medium text-gray-700 mb-1.5">
                  CUG Number or Email
                </label>
                <div className="relative">
                  <Phone className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" aria-hidden="true" />
                  <input
                    id="login-cug"
                    type="text"
                    value={loginId}
                    onChange={(e) => setLoginId(e.target.value)}
                    className="login-input"
                    placeholder="CUG number or email address"
                    autoComplete="username"
                    required
                  />
                </div>
              </div>

              <div>
                <label htmlFor="login-password" className="block text-sm font-medium text-gray-700 mb-1.5">
                  Password
                </label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" aria-hidden="true" />
                  <input
                    id="login-password"
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="login-input pr-10"
                    placeholder="Enter your password"
                    autoComplete="current-password"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 focus-visible:ring-2 focus-visible:ring-blue-400 rounded"
                  >
                    {showPassword ? <EyeOff className="h-4 w-4" aria-hidden="true" /> : <Eye className="h-4 w-4" aria-hidden="true" />}
                  </button>
                </div>
                <div className="mt-1.5 text-right">
                  <button
                    type="button"
                    onClick={() => {
                      // Prefill when the login id is already a CUG number or a valid email.
                      const id = loginId.trim();
                      setForgotIdentifier(isCugNumber(id) || isAllowedEmail(id) ? id : '');
                      setView(VIEWS.FORGOT_ID);
                    }}
                    className="text-xs text-blue-600 hover:text-blue-800 font-semibold"
                  >
                    Forgot password?
                  </button>
                </div>
              </div>

              <button type="submit" disabled={loading} className="login-btn mt-2">
                {loading ? (
                  <span className="flex items-center justify-center gap-2">
                    <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" aria-hidden="true" />
                    Signing In...
                  </span>
                ) : 'Sign In'}
              </button>

              <div className="mt-4 p-3 bg-blue-50 border border-blue-100 rounded-lg">
                <p className="text-xs text-blue-700 text-center">
                  New registrations require administrator approval
                </p>
              </div>

              <div className="mt-5 text-center">
                <button
                  type="button"
                  onClick={() => setView(VIEWS.SIGNUP_FORM)}
                  className="text-sm text-blue-600 hover:text-blue-800 font-semibold transition-colors focus-visible:ring-2 focus-visible:ring-blue-400 rounded px-1"
                >
                  Don't have an account? Sign Up
                </button>
              </div>
            </form>
          )}

          {/* =============================================== SIGNUP FORM view */}
          {view === VIEWS.SIGNUP_FORM && (
            <form onSubmit={handleSignupRequestOtp} className="space-y-4" noValidate>
              <div>
                <label htmlFor="signup-designation" className="block text-sm font-medium text-gray-700 mb-1.5">Designation</label>
                <DesignationSelect id="signup-designation" value={designation} onChange={setDesignation} />
              </div>

              <div>
                <label htmlFor="signup-cug" className="block text-sm font-medium text-gray-700 mb-1.5">CUG / Mobile Number</label>
                <div className="relative">
                  <Phone className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" aria-hidden="true" />
                  <input
                    id="signup-cug"
                    type="tel"
                    inputMode="numeric"
                    maxLength={10}
                    value={cugNumber}
                    onChange={(e) => setCugNumber(e.target.value.replace(/\D/g, '').slice(0, 10))}
                    className="login-input"
                    placeholder="10-digit CUG number"
                    autoComplete="tel"
                    required
                  />
                </div>
                <p className="mt-1 text-xs text-gray-400">An OTP will be sent to this number to verify it.</p>
              </div>

              <div>
                <label htmlFor="signup-email" className="block text-sm font-medium text-gray-700 mb-1.5">Official Email Address</label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" aria-hidden="true" />
                  <input
                    id="signup-email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="login-input"
                    placeholder="name@ksp.gov.in"
                    autoComplete="email"
                    required
                  />
                </div>
              </div>

              <div>
                <label htmlFor="signup-password" className="block text-sm font-medium text-gray-700 mb-1.5">Password</label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" aria-hidden="true" />
                  <input
                    id="signup-password"
                    type={showSignupPassword ? 'text' : 'password'}
                    value={signupPassword}
                    onChange={(e) => setSignupPassword(e.target.value)}
                    className="login-input pr-10"
                    placeholder="Choose a strong password"
                    autoComplete="new-password"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowSignupPassword(!showSignupPassword)}
                    aria-label={showSignupPassword ? 'Hide password' : 'Show password'}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    {showSignupPassword ? <EyeOff className="h-4 w-4" aria-hidden="true" /> : <Eye className="h-4 w-4" aria-hidden="true" />}
                  </button>
                </div>
              </div>

              <div>
                <label htmlFor="signup-confirm" className="block text-sm font-medium text-gray-700 mb-1.5">Confirm Password</label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" aria-hidden="true" />
                  <input
                    id="signup-confirm"
                    type={showConfirmPassword ? 'text' : 'password'}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className="login-input pr-10"
                    placeholder="Confirm your password"
                    autoComplete="new-password"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    aria-label={showConfirmPassword ? 'Hide confirm password' : 'Show confirm password'}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    {showConfirmPassword ? <EyeOff className="h-4 w-4" aria-hidden="true" /> : <Eye className="h-4 w-4" aria-hidden="true" />}
                  </button>
                </div>
              </div>

              {signupPassword && <PasswordRules pw={signupPassword} />}

              <button type="submit" disabled={loading} className="login-btn mt-2">
                {loading ? (
                  <span className="flex items-center justify-center gap-2">
                    <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" aria-hidden="true" />
                    Sending OTP...
                  </span>
                ) : 'Send OTP'}
              </button>

              <div className="mt-3 text-center">
                <button
                  type="button"
                  onClick={() => setView(VIEWS.LOGIN)}
                  className="text-sm text-blue-600 hover:text-blue-800 font-semibold"
                >
                  Already have an account? Sign In
                </button>
              </div>
            </form>
          )}

          {/* =============================================== SIGNUP OTP view (mobile) */}
          {view === VIEWS.SIGNUP_OTP && (
            <form onSubmit={handleSignupVerifyMobile} className="space-y-4" noValidate>
              <div className="flex items-center justify-center gap-2 text-emerald-700 bg-emerald-50 border border-emerald-100 rounded-lg p-3">
                <ShieldCheck className="h-4 w-4" />
                <span className="text-xs">Step 1 of 2 — enter the OTP we sent via SMS to verify your mobile.</span>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">One-time Password</label>
                <OtpInput value={otpCode} onChange={setOtpCode} disabled={loading} />
                <div className="flex justify-between items-center mt-2 text-xs">
                  <span className="text-gray-500">
                    {otpExpirySecs > 0 ? `Expires in ${Math.floor(otpExpirySecs / 60)}:${String(otpExpirySecs % 60).padStart(2, '0')}` : 'OTP expired — request a new one'}
                  </span>
                  <button
                    type="button"
                    onClick={handleSignupResendOtp}
                    disabled={resendSecs > 0 || loading}
                    className="text-blue-600 hover:text-blue-800 font-semibold disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    {resendSecs > 0 ? `Resend in ${resendSecs}s` : 'Resend OTP'}
                  </button>
                </div>
              </div>

              <button type="submit" disabled={loading || otpCode.length < 4} className="login-btn mt-2">
                {loading ? (
                  <span className="flex items-center justify-center gap-2">
                    <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" aria-hidden="true" />
                    Verifying...
                  </span>
                ) : 'Verify Mobile & Continue'}
              </button>

              <p className="text-xs text-gray-500 text-center">
                Wrong number?{' '}
                <button type="button" onClick={() => setView(VIEWS.SIGNUP_FORM)} className="text-blue-600 hover:text-blue-800 font-semibold">
                  Edit details
                </button>
              </p>
            </form>
          )}

          {/* ========================================= SIGNUP EMAIL OTP view */}
          {view === VIEWS.SIGNUP_EMAIL_OTP && (
            <form onSubmit={handleSignupVerifyEmailAndRegister} className="space-y-4" noValidate>
              <div className="flex items-center justify-center gap-2 text-emerald-700 bg-emerald-50 border border-emerald-100 rounded-lg p-3">
                <Mail className="h-4 w-4" />
                <span className="text-xs">Step 2 of 2 — enter the OTP we emailed to verify your email address.</span>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Email One-time Password</label>
                <OtpInput value={otpCode} onChange={setOtpCode} disabled={loading} />
                <div className="flex justify-between items-center mt-2 text-xs">
                  <span className="text-gray-500">
                    {otpExpirySecs > 0 ? `Expires in ${Math.floor(otpExpirySecs / 60)}:${String(otpExpirySecs % 60).padStart(2, '0')}` : 'OTP expired — request a new one'}
                  </span>
                  <button
                    type="button"
                    onClick={handleSignupResendEmailOtp}
                    disabled={resendSecs > 0 || loading}
                    className="text-blue-600 hover:text-blue-800 font-semibold disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    {resendSecs > 0 ? `Resend in ${resendSecs}s` : 'Resend OTP'}
                  </button>
                </div>
              </div>

              <button type="submit" disabled={loading || otpCode.length < 4} className="login-btn mt-2">
                {loading ? (
                  <span className="flex items-center justify-center gap-2">
                    <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" aria-hidden="true" />
                    Verifying...
                  </span>
                ) : 'Verify Email & Create Account'}
              </button>

              <p className="text-xs text-gray-500 text-center">
                Wrong email?{' '}
                <button type="button" onClick={() => { setOtpCode(''); setView(VIEWS.SIGNUP_FORM); }} className="text-blue-600 hover:text-blue-800 font-semibold">
                  Edit details
                </button>
              </p>
            </form>
          )}

          {/* =============================================== FORGOT ID view */}
          {view === VIEWS.FORGOT_ID && (
            <form onSubmit={handleForgotSendOtp} className="space-y-4" noValidate>
              <div>
                <label htmlFor="forgot-id" className="block text-sm font-medium text-gray-700 mb-1.5">CUG Number or Email</label>
                <div className="relative">
                  {looksLikeEmail(forgotIdentifier) ? (
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" aria-hidden="true" />
                  ) : (
                    <Phone className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" aria-hidden="true" />
                  )}
                  <input
                    id="forgot-id"
                    type="text"
                    maxLength={120}
                    value={forgotIdentifier}
                    onChange={(e) => setForgotIdentifier(e.target.value)}
                    className="login-input"
                    placeholder="10-digit CUG number or email address"
                    autoComplete="username"
                    required
                  />
                </div>
                <p className="mt-1 text-xs text-gray-400">
                  {looksLikeEmail(forgotIdentifier)
                    ? "We'll email the OTP to this address."
                    : isCugNumber(forgotIdentifier)
                      ? "We'll text the OTP to this mobile number."
                      : 'Enter your registered CUG number to get the OTP by SMS, or your email to get it by email.'}
                </p>
              </div>

              <button type="submit" disabled={loading} className="login-btn mt-2">
                {loading ? (
                  <span className="flex items-center justify-center gap-2">
                    <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" aria-hidden="true" />
                    Sending OTP...
                  </span>
                ) : 'Send OTP'}
              </button>

              <div className="mt-3 text-center">
                <button
                  type="button"
                  onClick={() => setView(VIEWS.LOGIN)}
                  className="text-sm text-blue-600 hover:text-blue-800 font-semibold"
                >
                  Back to Sign In
                </button>
              </div>
            </form>
          )}

          {/* =============================================== FORGOT OTP view */}
          {view === VIEWS.FORGOT_OTP && (
            <form onSubmit={handleForgotVerifyOtp} className="space-y-4" noValidate>
              <div className="flex items-center justify-center gap-2 text-emerald-700 bg-emerald-50 border border-emerald-100 rounded-lg p-3">
                <ShieldCheck className="h-4 w-4" />
                <span className="text-xs">
                  {forgotChannel === 'email'
                    ? 'Enter the OTP from the email sent to your registered address.'
                    : 'Enter the OTP from the SMS sent to your registered mobile.'}
                </span>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">One-time Password</label>
                <OtpInput value={otpCode} onChange={setOtpCode} disabled={loading} />
                <div className="flex justify-between items-center mt-2 text-xs">
                  <span className="text-gray-500">
                    {otpExpirySecs > 0 ? `Expires in ${Math.floor(otpExpirySecs / 60)}:${String(otpExpirySecs % 60).padStart(2, '0')}` : 'OTP expired — request a new one'}
                  </span>
                  <button
                    type="button"
                    onClick={handleForgotResendOtp}
                    disabled={resendSecs > 0 || loading}
                    className="text-blue-600 hover:text-blue-800 font-semibold disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    {resendSecs > 0 ? `Resend in ${resendSecs}s` : 'Resend OTP'}
                  </button>
                </div>
              </div>

              <button type="submit" disabled={loading || otpCode.length < 4} className="login-btn mt-2">
                {loading ? (
                  <span className="flex items-center justify-center gap-2">
                    <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" aria-hidden="true" />
                    Verifying...
                  </span>
                ) : 'Verify OTP'}
              </button>
            </form>
          )}

          {/* =============================================== FORGOT RESET view */}
          {view === VIEWS.FORGOT_RESET && (
            <form onSubmit={handleForgotReset} className="space-y-4" noValidate>
              <div>
                <label htmlFor="reset-password" className="block text-sm font-medium text-gray-700 mb-1.5">New Password</label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" aria-hidden="true" />
                  <input
                    id="reset-password"
                    type={showResetPassword ? 'text' : 'password'}
                    value={resetPassword}
                    onChange={(e) => setResetPassword(e.target.value)}
                    className="login-input pr-10"
                    placeholder="Choose a strong password"
                    autoComplete="new-password"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowResetPassword(!showResetPassword)}
                    aria-label={showResetPassword ? 'Hide password' : 'Show password'}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    {showResetPassword ? <EyeOff className="h-4 w-4" aria-hidden="true" /> : <Eye className="h-4 w-4" aria-hidden="true" />}
                  </button>
                </div>
              </div>

              <div>
                <label htmlFor="reset-confirm" className="block text-sm font-medium text-gray-700 mb-1.5">Confirm New Password</label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" aria-hidden="true" />
                  <input
                    id="reset-confirm"
                    type={showResetConfirm ? 'text' : 'password'}
                    value={resetConfirm}
                    onChange={(e) => setResetConfirm(e.target.value)}
                    className="login-input pr-10"
                    placeholder="Confirm your password"
                    autoComplete="new-password"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowResetConfirm(!showResetConfirm)}
                    aria-label={showResetConfirm ? 'Hide confirm password' : 'Show confirm password'}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    {showResetConfirm ? <EyeOff className="h-4 w-4" aria-hidden="true" /> : <Eye className="h-4 w-4" aria-hidden="true" />}
                  </button>
                </div>
              </div>

              {resetPassword && <PasswordRules pw={resetPassword} />}

              <button
                type="submit"
                disabled={loading || !isStrongPassword(resetPassword) || resetPassword !== resetConfirm}
                className="login-btn mt-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? (
                  <span className="flex items-center justify-center gap-2">
                    <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" aria-hidden="true" />
                    Resetting...
                  </span>
                ) : 'Reset Password'}
              </button>
            </form>
          )}

          <p className="md:hidden text-center text-xs text-gray-400 mt-6">&copy; 2026 CCITR. All rights reserved.</p>
        </div>
      </motion.div>
    </div>
  );
};

export default Login;
